import {
  Card,
  CardHeader,
  CardTitle,
  EventType,
  Timeline,
  TimelineEvent,
  TimelineEventAction,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  DatePickerWithRange,
  LoadingSpinner,
  ScrollArea,
} from '@ais-ui/ui-core';
import { cn } from '@ais-ui/utils';
import { DateRange } from 'react-day-picker';

export type TimelineContainerProps = {
  isLoading: boolean;
  events: TimelineEvent[];
  eventTypes: EventType[]; // All possible event types
  className?: string;
  clusterThreshold?: number; // Distance threshold for clustering (in percentage)
  showLegend?: boolean; // Whether to show the legend
  title: string; // Title for the timeline
  dateRange?: DateRange | undefined;
  onDateRangeChange?: (dateRange: DateRange | undefined) => void;
  emptyMessage?: string; // Message to show when there are no events or an error occurred
  eventActions?: TimelineEventAction[]; // Optional array of actions for events
};

export const TimelineContainer = ({
  isLoading,
  events,
  eventTypes,
  className,
  clusterThreshold = 5,
  showLegend = true,
  title,
  dateRange,
  onDateRangeChange,
  emptyMessage,
  eventActions,
}: TimelineContainerProps) => {
  // Check if date range is not selected
  const noDateRangeSelected = !dateRange?.from && !dateRange?.to;

  return (
    <ScrollArea className="h-full">
      <Card className="p-4 space-y-2">
        <CardHeader>
          <div className="flex justify-between w-full">
            <div className="flex flex-col space-y-2">
              <CardTitle>{title}</CardTitle>
              {onDateRangeChange && (
                <div className="w-80">
                  <DatePickerWithRange
                    date={dateRange}
                    setDate={onDateRangeChange}
                  />
                </div>
              )}
            </div>
            {showLegend && (
              <div className="pb-10 flex flex-wrap gap-4 justify-center pr-4">
                {eventTypes.map((type) => (
                  <Tooltip key={type.id}>
                    <TooltipTrigger>
                      <div
                        key={type.id}
                        className={cn(
                          'flex items-center gap-2 cursor-default',
                          type.disabled && 'opacity-50'
                        )}
                      >
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: type.color }}
                        />
                        <span className="text-sm">{type.name}</span>
                      </div>
                    </TooltipTrigger>
                    {type.disabled && (
                      <TooltipContent>Missing permission</TooltipContent>
                    )}
                  </Tooltip>
                ))}
              </div>
            )}
          </div>
        </CardHeader>
        {isLoading ? (
          <Card>
            <CardHeader className="min-h-[112px]">
              <div className="flex space-x-2 items-center justify-center py-36 h-full">
                <LoadingSpinner></LoadingSpinner>
              </div>
            </CardHeader>
          </Card>
        ) : noDateRangeSelected ? (
          <div className="min-h-[264px] flex items-center justify-center w-full text-muted-foreground">
            Choose a date range to view timeline events.
          </div>
        ) : events.length === 0 && emptyMessage ? (
          <div className="min-h-[264px] flex items-center justify-center w-full text-muted-foreground">
            {emptyMessage}
          </div>
        ) : (
          <Timeline
            events={events}
            eventTypes={eventTypes}
            className={className}
            clusterThreshold={clusterThreshold}
            dateRange={dateRange}
            eventActions={eventActions}
          />
        )}
      </Card>
    </ScrollArea>
  );
};
